import classNames from "classnames"
import Action from "components/Action"
import { ChevronLeft, ChevronRight } from "components/Icons"
import style from "components/Slider.module.scss"
import Video from "components/Video"
import { AnimatePresence, motion } from "framer-motion"
import Img from "gatsby-image"
import { contentVariants } from "lib/constants"
import { formatMarkdown } from "lib/helpers"
import React, { useCallback, useState } from "react"

import { wrap } from "@popmotion/popcorn"

const cellVariants = {
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
}

const Slider = ({ cells }) => {
  const [[cellIndex, direction], setCellIndex] = useState([0, 0])
  const activeCellIndex = wrap(0, cells.length, cellIndex)
  const activeCell = cells[activeCellIndex]

  const paginate = useCallback(
    newDirection => {
      setCellIndex([cellIndex + newDirection, newDirection])
    },
    [cellIndex]
  )

  const handlePagerClick = index => {
    setCellIndex([index, 1])
  }

  const Pager = () => {
    return (
      <>
        {cells.length > 1 && (
          <div className={style.pager}>
            {cells.map((cell, i) => {
              const buttonClassName = classNames(style.pagerButton, {
                [style.pagerButton___isActive]: activeCell.id === cell.id
              })
              return (
                <button
                  key={cell.id}
                  onClick={() => handlePagerClick(i)}
                  type="button"
                  className={buttonClassName}
                  aria-label={`Go to cell ${i + 1}`}
                />
              )
            })}
          </div>
        )}
      </>
    )
  }

  const Controls = () => {
    return (
      <>
        <button
          onClick={() => paginate(-1)}
          className={[style.control, style.control___prev].join(" ")}
          type="button"
        >
          <ChevronLeft />
        </button>
        <button
          onClick={() => paginate(1)}
          className={[style.control, style.control___next].join(" ")}
          type="button"
        >
          <ChevronRight />
        </button>
      </>
    )
  }

  const className = classNames(style.root)

  return (
    <div className={className}>
      <motion.div className={style.content} variants={contentVariants}>
        <div className={style.cellWrapper}>
          <AnimatePresence exitBeforeEnter>
            {!!activeCell && <Cell key={activeCell.id} cell={activeCell} />}
          </AnimatePresence>
        </div>
        <Pager />
        <Controls />
      </motion.div>
    </div>
  )
}

const Cell = ({ cell }) => {
  const text = cell.text ? formatMarkdown(cell.text.text) : null

  const hasVideo = !!cell.videoUrl

  return (
    <motion.div
      className={style.cell}
      variants={cellVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className={style.media}>
        {!hasVideo && (
          <div className={style.image}>
            {!!cell.image && (
              <Img
                placeholderStyle={{ visibility: "hidden" }}
                fluid={cell.image?.fluid}
              />
            )}
          </div>
        )}
        {hasVideo && (
          <div className={style.video}>
            <Video posterImage={cell.image} videoUrl={cell.videoUrl} />
          </div>
        )}
      </div>
      <div className={style.captionWrapper}>
        <div className={style.caption}>
          <h3 className={style.heading}>{cell.heading}</h3>
          {!!text && (
            <div
              className={style.text}
              dangerouslySetInnerHTML={{
                __html: text
              }}
            />
          )}
          {(cell.label || cell.url) && (
            <div className={style.action}>
              <Action
                label={cell.actionLabel}
                url={cell.actionUrl}
                backgroundColor="white"
              />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default Slider
